import { getCategoryTreeFromCrumbs } from '@/integrations/_helpers/getCategoryTreeFromCrumbs'
import { getCategoryTreeSlug } from '@/integrations/_helpers/getCategoryTreeSlugFromAlgolia'
import { getFreeShippingText, getMadeToOrderText, getTimeRemainingText } from '@/helpers/shipping'

export default function wireProductListeners(segment, ctx) {
    const { $uiEvents, store } = ctx
    $uiEvents.$on('selectItemError', emitSelectItemError)
    $uiEvents.$on('backInStockSubscribed', emitBackInStockSubscribed)

    $uiEvents.$on('addToCart', emitAddToCart)
    $uiEvents.$on('removeFromCart', emitRemoveFromCart)

    $uiEvents.$on('promoCodeEntered', emitPromoCodeEntered)
    $uiEvents.$on('promoCodeApplied', emitPromoCodeApplied)
    $uiEvents.$on('promoCodeDenied', emitPromoCodeDenied)
    $uiEvents.$on('promoCodeRemoved', emitPromoCodeRemoved)

    $uiEvents.$on('productListClicked', emitProductListClicked)
    $uiEvents.$on('productListViewed', emitProductListViewed)
    $uiEvents.$on('productListFiltered', emitProductListFiltered)
    $uiEvents.$on('productViewed', emitProductViewed)

    function emitSelectItemError({ type, error, extra }) {
        error = typeof error === 'undefined' ? {} : error
        extra = typeof extra === 'undefined' ? {} : extra

        const event = Object.assign(
            {
                type, // NETWORK_ERROR, UNEXPECTED_RESULT, UKNOWN
                ...(error.config && { url: error.config.url }),
                ...(error.config && { requestData: error.config.data }),
                ...(error.response && { status: error.response.status }),
                ...(error.response && { errors: error.response.data.errors }),
            },
            extra
        )

        segment.track('Select Item Error', event)
    }

    function emitBackInStockSubscribed({ skuSlug, email }) {
        const primaryFitment = segment.getFitment()

        segment.track('Back In Stock', {
            skuSlug,
            email,
            primaryFitment,
        })
    }

    async function emitAddToCart(lastItem) {
        if (!lastItem) return

        const product = segment.getCartProduct(lastItem)
        const algoliaInfo = segment.getAlgoliaQueryInfo()

        const objectID = await segment.getAlgoliaObjectId(lastItem)
        segment.track('Product Added', {
            cart_id: store.state.permId,
            ...product,
            ...algoliaInfo,
            ...(objectID && { objectID }),
        })
    }

    function emitRemoveFromCart(lastItem) {
        if (!lastItem) return
        const product = segment.getCartProduct(lastItem)
        segment.track('Product Removed', { cart_id: store.state.permId, ...product })
    }

    function emitPromoCodeEntered(code) {
        segment.track('Coupon Entered', {
            cart_id: store.state.permId,
            coupon_id: code,
            // order_id
        })
    }

    function emitPromoCodeApplied(code) {
        segment.track('Coupon Applied', {
            cart_id: store.state.permId,
            coupon_id: code,
            // order_id
            // discount
            // coupon_name
        })
    }

    function emitPromoCodeDenied(code) {
        segment.track('Coupon Denied', {
            cart_id: store.state.permId,
            coupon_id: code,
            // order_id
            // reason
        })
    }

    function emitPromoCodeRemoved(code) {
        segment.track('Coupon Removed', {
            cart_id: store.state.permId,
            coupon_id: code,
            // order_id
            // discount
            // coupon_name
        })
    }

    function emitProductListClicked({ algoliaObj, position, indexName, queryID }) {
        const productObj = getAlgoliaProduct(algoliaObj, position + 1)
        segment.track('Product Clicked', {
            ...(indexName && { index: indexName }),
            ...(queryID && { queryID }),
            ...productObj,
        })
    }

    // when a product square is viewed (currently just the whole result, not on viewport)
    function emitProductListViewed({ results, indexName, queryID }) {
        segment.track('Product List Viewed', {
            list_id: 'main',
            ...(indexName && { index: indexName }),
            ...(queryID && { queryID }),
            // category - We get individual products, but no main category to put here
            products: getAlgoliaProducts(results),
        })
    }

    function emitProductListFiltered({ filters, sort, products }) {
        // Determine Filters
        const filterObj = Object.keys(filters).reduce((acc, attributeName) => {
            const values = filters[attributeName].map((attribute) => ({
                type: attributeName,
                value: attribute.value,
            }))
            acc.push(...values)
            return acc
        }, [])

        // Determine Sort from indexName
        let sortType = null
        let sortDirection = null
        if (sort) {
            sortType = sort.includes('price') ? 'price' : 'recommended'
            if (sort.includes('_desc')) sortDirection = 'desc'
            else if (sort.includes('_asc')) sortDirection = 'asc'
        }

        segment.track('Product List Filtered', {
            list_id: 'main',
            filters: filterObj,
            ...(sort && {
                sorts: [
                    {
                        type: sortType,
                        value: sortDirection,
                    },
                ],
            }),
            products: getAlgoliaProducts(products),
        })
    }

    async function emitProductViewed({ location, index }) {
        const product =
            location === 'main'
                ? store.state.product.data
                : store.state.product.controls.groups[index].product

        if (!product) return
        emitProductShipping(product)

        const viewedProductObj = segment.formatProductData(product)
        const algoliaInfo = segment.getAlgoliaQueryInfo()
        const objectID = await segment.getAlgoliaObjectId(product)

        segment.track('Product Viewed', {
            ...viewedProductObj,
            ...algoliaInfo,
            ...(objectID && { objectID }),
        })
    }

    function emitProductShipping(product) {
        if (!product.shipping) return

        const sameDayObj = getTimeRemainingText(product.shipping)
        const madeToOrderText = getMadeToOrderText(product.isMadeToOrder)

        segment.track('Customer Shipping Expectations', {
            ...(product.sku && { sku: product.sku, label: product.sku }),
            product: product.name,
            ...(product.actualPrice && { price: product.actualPrice.min / 100 }),
            brand: product.brand.name,
            product_category: getCategoryTreeFromCrumbs(product.crumbs).slugs.join('/'),
            ...(product.shipping.sameDay && { shipping_same_day_object: sameDayObj }),
            ...(product.shipping.sameDay &&
                sameDayObj.orderText && { shipping_same_day_message: sameDayObj.orderText }),
            ...(madeToOrderText && { made_to_order: madeToOrderText }),
            // shipping text only shows for products that are not sameDay
            // so we need to make sure we send the proper value here because of it.
            ...(!product.shipping.sameDay &&
                product.shipping.isEligibleFreeShipping && {
                    shipping_text: getFreeShippingText(product.shipping),
                }),
            category: 'Customer Messages',
            action: 'Customer Shipping Expectations',
        })
    }

    function getAlgoliaProducts(algoliaObjs, useProductLineSlug = false) {
        return algoliaObjs.map((product, index) => {
            return getAlgoliaProduct(product, index + 1, useProductLineSlug)
        })
    }

    function getAlgoliaProduct(product, position = 1, useProductLineSlug = false) {
        const isQuickshipable = segment.isQuickship(product)
        const primaryFitment = segment.getFitment()

        return {
            objectID: product.objectID,
            product_id: useProductLineSlug ? product.productLineSlug : product.productId,
            sku: product.skuSlug,
            category: getCategoryTreeSlug(product.categories),
            name: product.productLineName,
            brand: product.brandSlug,
            price: product.salePrice / 100,
            quantity: 1,
            position,
            ...(isQuickshipable !== null && { quickship: isQuickshipable }),
            ...(primaryFitment && primaryFitment),
        }
    }
}
